import React, { useEffect, useRef } from 'react';
import { Field, FieldProps, useFormikContext } from 'formik';
import { Input } from 'syngenta-digital-cropwise-react-ui-kit';
import AlertExclamationIcon from 'assets/alert-exclamation.svg?react';
import { validateTagName } from '../../schema';

interface TagInputFieldProps {
  tag: {
    id: string;
    name: string;
    backgroundColor: string;
    textColor: string;
    color: string;
  };
  fieldName: string;
  formValues: any;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  isNewTag?: boolean;
  originalName?: string;
}

const TagInputField: React.FC<TagInputFieldProps> = ({
  tag,
  fieldName,
  formValues,
  onKeyDown,
  isNewTag = false,
  originalName = ''
}) => {
  const { setFieldTouched } = useFormikContext();

  // Always reflects the latest formValues, avoiding stale closure in validate
  const formValuesRef = useRef(formValues);
  formValuesRef.current = formValues;

  // Focus the input if it's a new tag
  useEffect(() => {
    if (!isNewTag) return undefined;
    // Small delay to ensure the component is fully rendered
    const focusTimer = setTimeout(() => {
      const input = document.querySelector<HTMLInputElement>(
        `input[name="${fieldName}"]`
      );
      if (input) {
        input.focus();
      }
    }, 100);
    // Clear on unmount: the timer otherwise outlives the component and
    // dereferences `document` after teardown (crashes the test runner).
    return () => clearTimeout(focusTimer);
  }, [isNewTag, fieldName]);

  return (
    <Field
      name={fieldName}
      validate={(value: string) => {
        const tagData = formValuesRef.current.tags[tag.id];
        const source = tagData?.source;

        const basicError = validateTagName(value, source);
        if (basicError) return basicError;
        if (!value || value.trim() === '') return undefined;

        const trimmedValue = value.trim().toLowerCase();

        // If this tag's name hasn't changed from its original, it's not the duplicate culprit
        if (trimmedValue === originalName?.trim().toLowerCase()) {
          return undefined;
        }

        // This tag was changed (or is new): error if any other tag has the same name
        const hasDuplicate = Object.keys(formValuesRef.current.tags).some(
          id =>
            id !== tag.id &&
            formValuesRef.current.tags[id]?.name?.trim().toLowerCase() ===
              trimmedValue
        );

        return hasDuplicate
          ? 'A tag with this name already exists.'
          : undefined;
      }}
    >
      {({ field, meta }: FieldProps) => {
        const hasError = meta.touched && meta.error;

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          formValuesRef.current = {
            ...formValuesRef.current,
            tags: {
              ...formValuesRef.current.tags,
              [tag.id]: {
                ...formValuesRef.current.tags[tag.id],
                name: e.target.value
              }
            }
          };
          setFieldTouched(fieldName, true, false);
          field.onChange(e);
        };

        return (
          <Input
            name={field.name}
            value={field.value}
            onChange={handleChange}
            onBlur={field.onBlur}
            onKeyDown={onKeyDown}
            className="w-full"
            style={{ width: '318px' }}
            status={hasError ? 'error' : undefined}
            suffix={
              // Always render suffix to keep the affix-wrapper structure stable.
              // When no error, use a zero-size element so no extra padding is added.
              hasError ? (
                <AlertExclamationIcon />
              ) : (
                <span style={{ display: 'block', width: 0, height: 0 }} />
              )
            }
            size="large"
          />
        );
      }}
    </Field>
  );
};

export default TagInputField;
